import {
  forwardRef,
  useId,
  useMemo,
  useState,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn.js';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  leftIcon?: ReactNode;
}

// Unguessable name so password managers (Firefox built-in, 1Password, LastPass…)
// can't pattern-match on `name="email"` / `name="password"`.
function randomName() {
  return 'pv-' + Math.random().toString(36).slice(2, 10);
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  // We deliberately drop any incoming `autoComplete` and `name` so password managers / browser
  // autofill can never attach to fields in this app — passphrases must never be stored anywhere.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  {
    label,
    hint,
    error,
    leftIcon,
    className,
    id,
    type,
    autoComplete: _ignoredAc,
    name: _ignoredName,
    onFocus,
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const isPassword = type === 'password';
  const forcedAutoComplete = isPassword ? 'new-password' : 'off';
  const randomFieldName = useMemo(randomName, []);

  // The `readonly until focus` trick neutralizes Firefox's built-in password manager,
  // which ignores `autocomplete="off"` on password fields. The user perceives no change:
  // tab/click focuses the field and immediately lifts readOnly.
  const [readOnly, setReadOnly] = useState(true);
  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setReadOnly(false);
    onFocus?.(e);
  };

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <label
          htmlFor={inputId}
          className="text-xs font-semibold uppercase tracking-[0.08em] text-on-surface-variant"
        >
          {label}
        </label>
      ) : null}
      <div
        className={cn(
          'flex items-center gap-3 rounded-xl border bg-surface-container-lowest px-4 py-3.5',
          'transition-all duration-150',
          'focus-within:border-cyan focus-within:ring-4 focus-within:ring-cyan/15',
          error
            ? 'border-error focus-within:border-error focus-within:ring-error/15'
            : 'border-outline hover:border-on-surface-variant/40',
        )}
      >
        {leftIcon ? <span className="text-on-surface-variant">{leftIcon}</span> : null}
        <input
          ref={ref}
          id={inputId}
          type={type}
          name={randomFieldName}
          autoComplete={forcedAutoComplete}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          readOnly={readOnly}
          onFocus={handleFocus}
          aria-autocomplete="none"
          data-1p-ignore="true"
          data-lpignore="true"
          data-bwignore="true"
          data-form-type="other"
          className={cn(
            'min-w-0 flex-1 bg-transparent text-[15px] text-on-surface placeholder:text-on-surface-variant/50',
            'focus:outline-none read-only:cursor-text',
            className,
          )}
          {...rest}
        />
      </div>
      {error ? (
        <p className="text-xs text-error">{error}</p>
      ) : hint ? (
        <p className="text-xs text-on-surface-variant">{hint}</p>
      ) : null}
    </div>
  );
});
